import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { leaderboardApi } from '@/services/api';
import { USE_MOCK_DATA } from '@/lib/constants';

export type LeaderboardScope = 'global' | 'friends' | 'league';
export type LeaderboardSort = 'rank' | 'points' | 'accuracy';
export type Trend = 'up' | 'down' | 'flat';

export interface LeaderboardRow {
  rank: number;
  userId: string;
  username: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  favoriteTeamId?: string | null;
  score: number;
  correctPicks: number;
  totalPicks: number;
  accuracy: number; // 0–100
  streak: number;
  upsets: number;
  trend: Trend;
  isMe: boolean;
}

export interface MyEntry {
  rank: number;
  score: number;
  correctPicks: number;
  totalPicks: number;
  accuracy: number;
  percentile: number;
}

export interface LeaderboardMeta {
  scope: LeaderboardScope;
  sort: LeaderboardSort;
  total: number;
  limit: number;
  offset: number;
}

export interface LeaderboardResponse {
  rows: LeaderboardRow[];
  myEntry: MyEntry | null;
  meta: LeaderboardMeta;
}

interface LeaderboardState {
  data: LeaderboardResponse | null;
  isLoading: boolean;
  error: string | null;

  fetch: (params: {
    scope: LeaderboardScope;
    leagueId?: string;
    sort?: LeaderboardSort;
    limit?: number;
    offset?: number;
  }) => Promise<void>;
}

const mock: LeaderboardResponse = {
  rows: Array.from({ length: 20 }).map((_, i) => ({
    rank: i + 1,
    userId: `user-${i + 1}`,
    username: `BracketMaster${i + 1}`,
    displayName: null,
    avatarUrl: null,
    score: 1000 - i * 10,
    correctPicks: 45 - i,
    totalPicks: 63,
    accuracy: Math.max(0, 75 - i * 0.5),
    streak: Math.max(0, 20 - i),
    upsets: Math.max(0, 15 - Math.floor(i / 2)),
    trend: 'flat',
    isMe: i === 5,
  })),
  myEntry: {
    rank: 142,
    score: 680,
    correctPicks: 38,
    totalPicks: 63,
    accuracy: 60.3,
    percentile: 85.2,
  },
  meta: {
    scope: 'global',
    sort: 'rank',
    total: 4832,
    limit: 50,
    offset: 0,
  },
};

export const useLeaderboardStore = create<LeaderboardState>()(
  devtools(
    (set) => ({
      data: null,
      isLoading: false,
      error: null,

      fetch: async (params) => {
        set({ isLoading: true, error: null });
        try {
          if (USE_MOCK_DATA) {
            set({ data: { ...mock, meta: { ...mock.meta, scope: params.scope, sort: params.sort ?? 'rank' } }, isLoading: false });
            return;
          }
          const res = await leaderboardApi.get({
            scope: params.scope,
            leagueId: params.leagueId,
            sort: params.sort,
            limit: params.limit,
            offset: params.offset,
          });
          set({ data: res, isLoading: false });
        } catch (e: any) {
          set({ error: e?.message ?? 'Failed to load leaderboard', isLoading: false });
        }
      },
    }),
    { name: 'leaderboard-store' }
  )
);
